// Progress bilans (« Mes bilans », #/bilans). Session-local DEMO: new progress bilans live in memory (« Plus tard » keeps the
// position, a reload returns to the fixtures). The historical fixture (Bilan initial, 25 septembre) and the new initial bilan
// (bilan-state.ts) are READ, never copied. Measures follow the account's health-data consent, photos the progress-photo consent:
// a revocation clears them (object URLs released) wherever the member is. Nothing is sent, uploaded or diagnosed.
import { useSyncExternalStore } from "react";
import { account } from "./compte-state";
import { BILANS } from "./suivi-fixtures";
import { BILAN_DATE, COACH_MEASURES, PHOTO_SHOTS, bilan as initialBilan, type ShotId } from "./bilan-state";

export const MEASURES = [{ id: "poids", label: "Poids", unit: "kg" }, ...COACH_MEASURES] as const;
export type MeasureId = typeof MEASURES[number]["id"];
export { PHOTO_SHOTS, type ShotId };

export interface Shot { url?: string; skipped?: boolean }
export interface ProgressBilan {
  id: string; title: string; date: string; status: "draft" | "submitted";
  step: number; sub: number;
  /** "passer" = measures skipped at the consent screen; nothing kept. */
  measureChoice?: "mesures" | "passer";
  measures: Partial<Record<MeasureId, { value?: number; skipped?: boolean }>>;
  photoChoice?: "photos" | "passer";
  shots: Partial<Record<ShotId, Shot>>;
  comment: string;
}

interface State { list: ProgressBilan[] }
let state: State = { list: [] };
let counter = 0;
const listeners = new Set<() => void>();
const set = (next: State) => { state = next; listeners.forEach(l => l()); };

export const bilans = {
  get: () => state,
  subscribe: (l: () => void) => { listeners.add(l); return () => listeners.delete(l); },
  /** A new draft dated on the demo « today » (Monday 12 October). */
  create: (): string => {
    const id = `bp-${++counter}`;
    set({ list: [{ id, title: "Bilan de progression", date: BILAN_DATE, status: "draft", step: 0, sub: 0, measures: {}, shots: {}, comment: "" }, ...state.list] });
    return id;
  },
  patch: (id: string, patch: Partial<ProgressBilan>) => set({ list: state.list.map(b => b.id === id ? { ...b, ...patch } : b) }),
};
export const useBilans = () => useSyncExternalStore(bilans.subscribe, bilans.get, bilans.get);

/** Consent revocation: measures without the health-data consent, photos without the progress-photo consent. */
function syncConsents() {
  const c = account.get().consents;
  let changed = false;
  const list = state.list.map(b => {
    let next = b;
    if (!c.sensibles && (b.measureChoice === "mesures" || Object.keys(b.measures).length)) { next = { ...next, measures: {}, measureChoice: b.measureChoice === "mesures" ? undefined : b.measureChoice }; changed = true; }
    if (!c.photos && (b.photoChoice === "photos" || Object.keys(b.shots).length)) {
      Object.values(b.shots).forEach(s => { if (s?.url) URL.revokeObjectURL(s.url); });
      next = { ...next, shots: {}, photoChoice: b.photoChoice === "photos" ? undefined : b.photoChoice }; changed = true;
    }
    return next;
  });
  if (changed) set({ list });
}
account.subscribe(syncConsents);

/* ---------- Read model shared by the list and the detail (fixture, new initial, progress bilans) ---------- */

export interface BilanView {
  id: string; kind: "fixture" | "initial" | "progress"; title: string; date: string;
  status: "draft" | "submitted" | "commented"; author: string;
  measures: Partial<Record<MeasureId, number>>;
  shots: Partial<Record<ShotId, string>>;
  memberComment?: string;
  coach?: { name: string; date: string; comment: string; priorities?: string[] };
}

const parseKg = (v?: string) => { const n = v ? Number(v.replace(/[^\d,.]/g, "").replace(",", ".")) : NaN; return Number.isFinite(n) ? n : undefined; };

/** All bilans readable now, newest first. Values are only those really filled in, and only under the account's consents. */
export function views(): BilanView[] {
  const c = account.get().consents;
  const out: BilanView[] = [];
  for (const b of state.list) out.push({
    id: b.id, kind: "progress", title: b.title, date: b.date, status: b.status, author: "Toi",
    measures: c.sensibles ? Object.fromEntries(Object.entries(b.measures).flatMap(([k, v]) => v?.value !== undefined ? [[k, v.value]] : [])) : {},
    shots: c.photos ? Object.fromEntries(Object.entries(b.shots).flatMap(([k, v]) => v?.url ? [[k, v.url]] : [])) : {},
    memberComment: b.comment.trim() || undefined,
  });
  const ini = initialBilan.get();
  if (ini.status !== "todo") out.push({
    id: "initial", kind: "initial", title: "Bilan initial", date: BILAN_DATE, status: ini.status === "submitted" ? "submitted" : "draft", author: "Toi",
    measures: c.sensibles ? Object.fromEntries([
      ...(ini.weight?.kg !== undefined ? [["poids", ini.weight.kg]] : []),
      ...Object.entries(ini.measures).flatMap(([k, v]) => v?.value !== undefined ? [[k, v.value]] : []),
    ]) : {},
    shots: c.photos && ini.photoChoice === "photos" ? Object.fromEntries(Object.entries(ini.shots).flatMap(([k, v]) => v?.url ? [[k, v.url]] : [])) : {},
  });
  for (const f of BILANS.filter(x => x.done)) out.push({
    id: f.id, kind: "fixture", title: f.title, date: f.date, status: f.comment ? "commented" : "submitted", author: f.coach,
    measures: c.sensibles && parseKg(f.results?.find(r => r.label === "Poids")?.value) !== undefined ? { poids: parseKg(f.results?.find(r => r.label === "Poids")?.value)! } : {},
    shots: {},
    coach: f.comment ? { name: f.coach, date: f.date, comment: f.comment } : undefined,
  });
  return out.sort((a, b) => b.date.localeCompare(a.date) || (a.kind === "progress" ? -1 : 1));
}
